import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Plus,
  Pencil,
  Trash2,
  Wifi,
  CheckCircle2,
  XCircle,
  Loader2,
  AlertCircle,
  ClipboardCheck,
  ShoppingBag,
  Globe,
  ImageIcon,
  X,
  Link2,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import api from '../../services/api'
import type { AdminERP } from '../../types'
import { formatDate } from '../../utils/helpers'

type ModuleTestResult = { success: boolean; count?: number; error?: string }

export default function AdminERPs() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const currentUser = (() => {
    try {
      return JSON.parse(localStorage.getItem('user') ?? '{}')
    } catch {
      return {}
    }
  })()
  const isSuperAdmin = currentUser?.role === 'super_admin'

  // ERP admin: bypass the list and go directly to their own ERP settings
  useEffect(() => {
    if (!isSuperAdmin && currentUser?.erp_id) {
      navigate(`/admin/erps/${currentUser.erp_id}`, { replace: true })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const [erps, setErps] = useState<AdminERP[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [copiedSiren, setCopiedSiren] = useState<string | null>(null)
  const [copiedShopSiren, setCopiedShopSiren] = useState<string | null>(null)

  // Test connection modal
  const [testModalOpen, setTestModalOpen] = useState(false)
  const [testModalErpName, setTestModalErpName] = useState('')
  const [testModalLoading, setTestModalLoading] = useState(false)
  const [testModalResult, setTestModalResult] = useState<{
    success: boolean
    modules?: Record<string, ModuleTestResult>
    error?: string
  } | null>(null)

  const loadERPs = useCallback(async () => {
    try {
      const data = await api.getAdminERPs()
      setErps(Array.isArray(data) ? data : [])
      if (!Array.isArray(data))
        setError('Réponse API inattendue: ' + JSON.stringify(data).substring(0, 200))
    } catch (err: unknown) {
      const apiErr = err as { response?: { data?: { error?: string } }; message?: string }
      setError(t('common.error') + ': ' + (apiErr.response?.data?.error || apiErr.message))
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => {
    loadERPs()
  }, [loadERPs])

  const handleDelete = async (erp: AdminERP) => {
    if (!confirm(t('admin.erps.deleteConfirm', { name: erp.erp_name }))) return
    try {
      await api.deleteAdminERP(erp.id)
      await loadERPs()
    } catch (err: unknown) {
      const apiErr = err as { response?: { data?: { error?: string } } }
      alert(apiErr.response?.data?.error ?? t('common.error'))
    }
  }

  const openTestModal = async (erpId: number, erpName: string) => {
    setTestModalErpName(erpName)
    setTestModalResult(null)
    setTestModalLoading(true)
    setTestModalOpen(true)
    try {
      const result = await api.testERPConnection(erpId)
      setTestModalResult(result)
    } catch {
      setTestModalResult({ success: false, error: t('admin.erps.connectionFail') })
    } finally {
      setTestModalLoading(false)
    }
  }

  const buildLoginUrl = (siren: string) => {
    const base = (import.meta.env.BASE_URL || '/').replace(/\/$/, '')
    return `${window.location.origin}${base}/login/${siren.replace(/\s+/g, '')}`
  }

  const buildShopUrl = (siren: string) => {
    const base = (import.meta.env.BASE_URL || '/').replace(/\/$/, '')
    return `${window.location.origin}${base}/shop/${siren.replace(/\s+/g, '')}`
  }

  const handleCopyLoginLink = (siren: string) => {
    navigator.clipboard.writeText(buildLoginUrl(siren)).then(() => {
      setCopiedSiren(siren)
      setTimeout(() => setCopiedSiren(null), 2000)
    })
  }

  const handleCopyShopLink = (siren: string) => {
    navigator.clipboard.writeText(buildShopUrl(siren)).then(() => {
      setCopiedShopSiren(siren)
      setTimeout(() => setCopiedShopSiren(null), 2000)
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('admin.erps.title')}</h1>
          {!isSuperAdmin && (
            <p className="text-sm text-gray-500 mt-1">{t('admin.erps.readOnlyMode')}</p>
          )}
        </div>
        {isSuperAdmin && (
          <button
            onClick={() => navigate('/admin/erps/new')}
            className="flex items-center self-start justify-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus className="w-4 h-4" /> {t('admin.erps.addErp')}
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {/* Mobile card layout */}
        <ul className="divide-y divide-gray-100 sm:hidden">
          {erps.length === 0 ? (
            <li className="p-6 text-center text-gray-500">{t('admin.erps.none')}</li>
          ) : (
            erps.map((erp) => (
              <li key={erp.id} className="p-4 hover:bg-gray-50">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-3 min-w-0">
                    {erp.logo ? (
                      <img
                        src={erp.logo}
                        alt={erp.erp_name}
                        className="h-10 w-10 object-contain rounded-lg border border-gray-100 bg-gray-50 flex-shrink-0"
                      />
                    ) : (
                      <div className="h-10 w-10 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center flex-shrink-0">
                        <ImageIcon className="w-4 h-4 text-gray-300" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 truncate">{erp.erp_name}</p>
                      {erp.siren && (
                        <p className="text-xs text-gray-400 truncate">
                          {t('admin.erps.fieldSiren')} : {erp.siren}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                      {erp.erp_type}
                    </span>
                    <span
                      className={`px-2 py-0.5 text-xs font-medium rounded-full ${erp.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}
                    >
                      {erp.is_active ? t('common.active') : t('common.inactive')}
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs text-gray-500 truncate min-w-0">{erp.base_url}</p>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => openTestModal(erp.id, erp.erp_name)}
                      className="p-1.5 text-gray-400 hover:text-blue-600 rounded"
                      title={t('admin.erps.testConnection')}
                    >
                      <Wifi className="w-4 h-4" />
                    </button>
                    {isSuperAdmin && (
                      <>
                        <button
                          onClick={() => navigate(`/admin/erps/${erp.id}`)}
                          className="p-1.5 text-gray-400 hover:text-primary-600 rounded"
                          title={t('common.edit')}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(erp)}
                          className="p-1.5 text-gray-400 hover:text-red-600 rounded"
                          title={t('common.delete')}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </li>
            ))
          )}
        </ul>

        {/* Desktop table layout */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">
                  {t('admin.erps.colType')}
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">
                  {t('admin.erps.colLogo')}
                </th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">
                  {t('admin.erps.colName')}
                </th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">
                  {t('admin.erps.colUrl')}
                </th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">
                  {t('admin.erps.colSuppliers')}
                </th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">
                  {t('admin.erps.colStatus')}
                </th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">
                  {t('admin.erps.colDate')}
                </th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase">
                  {t('admin.erps.colActions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {erps.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center text-gray-500">
                    {t('admin.erps.none')}
                  </td>
                </tr>
              ) : (
                erps.map((erp) => (
                  <tr key={erp.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <span className="inline-block px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                        {erp.erp_type}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {erp.logo ? (
                        <img
                          src={erp.logo}
                          alt={erp.erp_name}
                          className="h-16 w-16 object-contain rounded-lg border border-gray-100 bg-gray-50"
                        />
                      ) : (
                        <div className="h-16 w-16 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center">
                          <ImageIcon className="w-6 h-6 text-gray-300" />
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-medium text-gray-900">{erp.erp_name}</p>
                      {erp.siren && (
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <p className="text-xs text-gray-400">
                            {t('admin.erps.fieldSiren')} : {erp.siren}
                          </p>
                          <button
                            onClick={() => handleCopyLoginLink(erp.siren!)}
                            title={
                              copiedSiren === erp.siren
                                ? t('admin.erps.loginLinkCopied')
                                : t('admin.erps.copyLoginLink')
                            }
                            className={`p-0.5 rounded transition-colors ${copiedSiren === erp.siren ? 'text-green-600' : 'text-gray-300 hover:text-primary-500'}`}
                          >
                            {copiedSiren === erp.siren ? (
                              <ClipboardCheck className="w-3.5 h-3.5" />
                            ) : (
                              <Link2 className="w-3.5 h-3.5" />
                            )}
                          </button>
                          <button
                            onClick={() => handleCopyShopLink(erp.siren!)}
                            title={
                              copiedShopSiren === erp.siren
                                ? t('admin.erps.shopLinkCopied')
                                : t('admin.erps.copyShopLink')
                            }
                            className={`p-0.5 rounded transition-colors ${copiedShopSiren === erp.siren ? 'text-green-600' : 'text-gray-300 hover:text-orange-500'}`}
                          >
                            {copiedShopSiren === erp.siren ? (
                              <ClipboardCheck className="w-3.5 h-3.5" />
                            ) : (
                              <ShoppingBag className="w-3.5 h-3.5" />
                            )}
                          </button>
                        </div>
                      )}
                      {erp.website && (
                        <a
                          href={erp.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-primary-500 hover:underline mt-0.5"
                          title={erp.website}
                        >
                          <Globe className="w-3 h-3" />
                          {erp.website.replace(/^https?:\/\//, '')}
                        </a>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                      {erp.base_url}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">{erp.customer_count ?? 0}</td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${erp.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}
                      >
                        {erp.is_active ? t('common.active') : t('common.inactive')}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {formatDate(erp.created_at)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openTestModal(erp.id, erp.erp_name)}
                          className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                          title={t('admin.erps.testConnection')}
                        >
                          <Wifi className="w-4 h-4" />
                        </button>
                        {isSuperAdmin && (
                          <>
                            <button
                              onClick={() => navigate(`/admin/erps/${erp.id}`)}
                              className="p-2 text-gray-400 hover:text-primary-600 transition-colors"
                              title={t('common.edit')}
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(erp)}
                              className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                              title={t('common.delete')}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Test Connection Modal */}
      {testModalOpen && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Wifi className="w-5 h-5 text-blue-600" />
                {t('admin.erps.testModalTitle', { name: testModalErpName })}
              </h2>
              <button
                onClick={() => setTestModalOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {testModalLoading ? (
                <div className="flex flex-col items-center justify-center py-8 gap-3 text-gray-500">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                  <span className="text-sm">{t('admin.erps.testTesting')}</span>
                </div>
              ) : testModalResult ? (
                <>
                  <div
                    className={`flex items-center gap-3 p-3 rounded-lg ${testModalResult.success ? 'bg-green-50' : 'bg-red-50'}`}
                  >
                    {testModalResult.success ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                    )}
                    <div>
                      <p
                        className={`text-sm font-medium ${testModalResult.success ? 'text-green-800' : 'text-red-800'}`}
                      >
                        {t('admin.erps.testConnectionStatus')}
                      </p>
                      <p
                        className={`text-xs mt-0.5 ${testModalResult.success ? 'text-green-700' : 'text-red-700'}`}
                      >
                        {testModalResult.success
                          ? t('admin.erps.connectionSuccess')
                          : (testModalResult.error ?? t('admin.erps.connectionFail'))}
                      </p>
                    </div>
                  </div>
                  {testModalResult.modules && Object.keys(testModalResult.modules).length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2">
                        {t('admin.erps.testModuleAccess')}
                      </p>
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase tracking-wider">
                              <th className="px-4 py-2">{t('admin.erps.testModuleCol')}</th>
                              <th className="px-4 py-2">{t('admin.erps.testStatusCol')}</th>
                              <th className="px-4 py-2 text-right">
                                {t('admin.erps.testCountCol')}
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {Object.entries(testModalResult.modules).map(([module, res]) => (
                              <tr key={module} className="hover:bg-gray-50">
                                <td className="px-4 py-2.5 font-medium text-gray-700">
                                  {t(`nav.${module}`, { defaultValue: module })}
                                </td>
                                <td className="px-4 py-2.5">
                                  {res.success ? (
                                    <span className="inline-flex items-center gap-1 text-green-700">
                                      <CheckCircle2 className="w-4 h-4" />
                                      {t('admin.erps.testOk')}
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1 text-red-600">
                                      <XCircle className="w-4 h-4" />
                                      {t('admin.erps.testFail')}
                                    </span>
                                  )}
                                  {!res.success && res.error && (
                                    <span className="ml-2 text-xs text-gray-400" title={res.error}>
                                      <AlertCircle className="w-3 h-3 inline" />
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-2.5 text-right text-gray-500">
                                  {res.success && res.count !== undefined ? (
                                    t('admin.erps.testModuleCount', { count: res.count })
                                  ) : res.error ? (
                                    <span
                                      className="text-xs text-red-500 line-clamp-1"
                                      title={res.error}
                                    >
                                      {res.error}
                                    </span>
                                  ) : (
                                    '—'
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              ) : null}
            </div>
            <div className="flex justify-end p-5 border-t border-gray-200">
              <button
                onClick={() => setTestModalOpen(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
